import { useCallback, useEffect, useState } from "react";
import useEmblaCarousel from "embla-carousel-react";
import { Briefcase, ChevronLeft, ChevronRight, Factory, MessageCircle } from "lucide-react";
import { useLang, WA_LINK } from "../i18n";
import { Chapter, Reveal } from "./Reveal";

export function MineralsCarousel() {
  const { t } = useLang();
  const [emblaRef, emblaApi] = useEmblaCarousel({ align: "start", loop: true, dragFree: true });
  const [canPrev, setCanPrev] = useState(true);
  const [canNext, setCanNext] = useState(true);

  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    setCanPrev(emblaApi.canScrollPrev());
    setCanNext(emblaApi.canScrollNext());
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    emblaApi.on("select", onSelect);
    onSelect();
  }, [emblaApi, onSelect]);

  return (
    <section id="minerals" data-testid="minerals-section" className="relative py-24 md:py-32 overflow-hidden">
      <div aria-hidden="true" className="absolute inset-0 overflow-hidden">
        <img src="/assets/bg-minerals.webp" alt="" loading="lazy" decoding="async" className="h-full w-full object-cover" />
        <div className="absolute inset-0 bg-[#07111F]/76" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#07111F] via-transparent to-[#07111F]" />
      </div>
      <div className="relative mx-auto max-w-7xl px-5 md:px-8">
        <div className="flex items-end justify-between gap-6">
          <Chapter kicker={t.minerals.kicker} title={t.minerals.title} subtitle={t.minerals.subtitle} testId="minerals" />
          <div className="hidden md:flex gap-3 mb-14">
            <button
              data-testid="minerals-prev-btn"
              aria-label={t.minerals.prev}
              disabled={!canPrev}
              onClick={() => emblaApi?.scrollPrev()}
              className="rounded-full border border-[#EDF1F6]/25 p-3 text-[#EDF1F6] hover:border-[#C9A227] hover:text-[#C9A227] transition-[border-color,color] duration-300"
            >
              <ChevronLeft size={20} />
            </button>
            <button
              data-testid="minerals-next-btn"
              aria-label={t.minerals.next}
              disabled={!canNext}
              onClick={() => emblaApi?.scrollNext()}
              className="rounded-full border border-[#EDF1F6]/25 p-3 text-[#EDF1F6] hover:border-[#C9A227] hover:text-[#C9A227] transition-[border-color,color] duration-300"
            >
              <ChevronRight size={20} />
            </button>
          </div>
        </div>
      </div>

      <Reveal>
        <div className="overflow-hidden" ref={emblaRef} data-testid="minerals-carousel">
          <div className="flex gap-5 px-5 md:px-8 py-4">
            {t.minerals.items.map((m, i) => (
              <article
                key={m.id}
                data-testid={`mineral-card-${m.id}`}
                className="group relative min-w-[270px] sm:min-w-[320px] glass overflow-hidden transition-[transform,box-shadow] duration-500 hover:-translate-y-2 hover:shadow-[0_20px_60px_rgba(201,162,39,0.12)]"
                style={{ borderRadius: "4px 28px 4px 28px" }}
              >
                <div className="relative h-52 overflow-hidden">
                  <img
                    src={m.img.replace(/\.(jpg|png)$/, ".webp")}
                    alt={m.name}
                    loading="lazy"
                    className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#151B24] via-transparent to-transparent" />
                  <span className="absolute top-3 left-3 font-display text-xs tracking-widest text-[#07111F] bg-[#C9A227] px-2.5 py-1 rounded-sm">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                </div>
                <div className="p-6">
                  <h3 className="font-display text-xl text-[#F5F7FA] group-hover:text-gold-metallic transition-colors duration-300">{m.name}</h3>
                  <p className="mt-2 text-sm text-[#EDF1F6] leading-relaxed min-h-[60px]">{m.desc}</p>
                  <p className="mt-4 flex items-start gap-2 text-xs text-[#EDF1F6]/80">
                    <Factory size={14} className="mt-0.5 shrink-0 text-[#C9A227]" />
                    <span><span className="uppercase tracking-wider text-[#C9A227]/90">{t.minerals.usesLabel}:</span> {m.uses}</span>
                  </p>
                  {m.comm && (
                    <p className="mt-2 flex items-start gap-2 text-xs text-[#EDF1F6]/80" data-testid={`mineral-comm-${m.id}`}>
                      <Briefcase size={14} className="mt-0.5 shrink-0 text-[#C9A227]" />
                      <span><span className="uppercase tracking-wider text-[#C9A227]/90">{t.minerals.commLabel}:</span> {m.comm}</span>
                    </p>
                  )}
                </div>
                <div className="pointer-events-none absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500" style={{ background: "radial-gradient(circle at 50% 0%, rgba(201,162,39,0.12), transparent 60%)" }} />
              </article>
            ))}
            <article
              data-testid="mineral-card-other"
              className="group relative min-w-[270px] sm:min-w-[320px] glass overflow-hidden border-dashed transition-[transform,box-shadow] duration-500 hover:-translate-y-2 hover:shadow-[0_20px_60px_rgba(201,162,39,0.12)]"
              style={{ borderRadius: "4px 28px 4px 28px" }}
            >
              <div className="flex h-full min-h-[280px] flex-col items-center justify-center p-8 text-center">
                <span className="flex h-14 w-14 items-center justify-center rounded-full border border-[#C9A227]/40 bg-[#C9A227]/10 text-[#C9A227]">
                  <MessageCircle size={24} />
                </span>
                <h3 className="mt-5 font-display text-xl uppercase tracking-widest text-[#F5F7FA]">{t.minerals.otherName}</h3>
                <p className="mt-3 text-sm text-[#EDF1F6] leading-relaxed">{t.minerals.otherDesc}</p>
                <a
                  data-testid="mineral-other-cta"
                  href={WA_LINK}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-6 inline-flex items-center gap-2 rounded-full border border-[#C9A227]/60 px-6 py-2.5 text-xs font-bold tracking-widest text-[#C9A227] hover:bg-[#C9A227] hover:text-[#07111F] transition-[background-color,color] duration-300"
                >
                  {t.minerals.otherCta}
                </a>
              </div>
            </article>
          </div>
        </div>
      </Reveal>
    </section>
  );
}
